import { Message } from "@apis:message/message";
import { post } from "@libs:axios";
import { camelizeObjectKeys } from "@utils:general";
import ndjsonStream from "can-ndjson-stream";
import { get } from "lodash";

interface StreamAIProps {
  setReferences: (data: Message["GET_ONE"]["Response"]["docs"]) => void;
  setAnswer: (data: (prev: string) => string) => void;
  setMessage?: (data: Message["GET_ONE"]["Response"]) => void;
  onNoResponse?: () => void;
}

interface AskAIStreamParams {
  question: string;
  botId: string;
  type: "search" | "chat";
  previousConversationId?: string;
}

export const useStreamAI = ({
  setReferences,
  setAnswer,
  setMessage,
  onNoResponse,
}: StreamAIProps) => {
  let isCancelled = false;
  let controller: AbortController;

  const askAIStream = async ({
    question,
    botId,
    type,
    previousConversationId,
  }: AskAIStreamParams) => {
    isCancelled = false;
    controller = new AbortController();
    const signal = controller.signal;

    const resp = await post<ReadableStream<Uint8Array>>(
      "/api/search/stream",
      {
        type,
        question,
        bot_id: botId,
        conversation_id:
          previousConversationId && !previousConversationId.startsWith("new")
            ? previousConversationId
            : null,
      },
      {
        signal,
        adapter: "fetch",
        responseType: "stream",
      },
    );

    const reader = ndjsonStream(resp).getReader();
    let result = await reader.read();

    if (result.done) {
      onNoResponse?.();
      return;
    }

    while (!result || !result.done || isCancelled) {
      if (!result.value) continue;

      const references = get(result.value, "docs");
      if (references) {
        setReferences(references);
      }

      const isMessageExist = get(result.value, "id");
      if (isMessageExist) {
        setMessage?.(
          camelizeObjectKeys(
            result.value,
          ) as unknown as Message["GET_ONE"]["Response"],
        );
      }

      const chunk = get(result.value, "chunk");
      if (chunk) {
        setAnswer((prev) => {
          if (prev.includes("●")) {
            return prev.slice(0, -1) + chunk + "●";
          } else {
            return prev + chunk + "●";
          }
        });
      }
      result = await reader.read();
    }
    // Remove the last dot after processing all elements
    setAnswer((prev) => prev.replace(/●$/, ""));
  };

  const cancel = async () => {
    controller?.abort();
    isCancelled = true;
  };

  return {
    askAIStream,
    cancel,
  };
};
